import { Layers, Wrench, Wind, Hammer, Anchor, Sparkles, ArrowUpRight } from "lucide-react";
const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const GOLD = "#F5A623";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Service = {
  number: string;
  title: string;
  desc: string;
  bullets: string[];
  icon: typeof Layers;
  span: "wide" | "normal";
  accent?: boolean;
};
const SERVICES: Service[] = [{
  number: "01",
  title: "Panel Replacement",
  desc: "Dented, cracked, or weather-damaged panel? We swap single panels — no need to replace the whole door. Colour-matched to your existing setup, 150+ panels in stock.",
  bullets: ["Single-panel replacement", "Colour & profile matched", "B&D, Steel-Line, Centurion stock"],
  icon: Layers,
  span: "wide",
  accent: true
}, {
  number: "02",
  title: "Roller & Hinge Service",
  desc: "Worn nylon rollers and dry hinges — the #1 cause of noisy doors.",
  bullets: ["Nylon roller swap", "Hinge replacement", "Lubrication service"],
  icon: Wrench,
  span: "normal"
}, {
  number: "03",
  title: "Track Re-alignment",
  desc: "Bent rails after vehicle impact — straightened or replaced.",
  bullets: ["Vertical & horizontal tracks", "Bracket re-anchoring", "Full alignment test"],
  icon: Anchor,
  span: "normal"
}, {
  number: "04",
  title: "Weather Seal Renewal",
  desc: "Bottom seals, side weatherstrips, and top seals — UV damage and perished rubber lets in cold, dust, water, and pests. We renew all seals to factory spec.",
  bullets: ["Bottom seal (most common)", "Side weatherstrips", "Top jamb seal"],
  icon: Wind,
  span: "wide"
}, {
  number: "05",
  title: "Top Bracket Repair",
  desc: "Top brackets fail silently — door can drop without warning.",
  bullets: ["Bracket replacement", "Cable re-attachment", "Safety inspection"],
  icon: Hammer,
  span: "normal"
}, {
  number: "06",
  title: "Annual Tune-Up",
  desc: "Lubrication, hardware check, balance test, safety inspection.",
  bullets: ["30-point inspection", "All hardware checked", "Written report"],
  icon: Sparkles,
  span: "normal"
}];
export default function SectionalServices() {
  return <section style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Atmospheric */}
      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      background: `radial-gradient(circle at 20% 0%, rgba(245,166,35,0.08) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(245,166,35,0.04) 0%, transparent 50%)`,
      pointerEvents: "none"
    }} />

      <div aria-hidden style={{
      position: "absolute",
      inset: 0,
      opacity: 0.03,
      mixBlendMode: "overlay",
      pointerEvents: "none",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")"
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1280,
      margin: "0 auto"
    }}>
        {/* === HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 72,
        alignItems: "end"
      }} className="sectional-services-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 20
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD
            }}>
                02 / Sectional services
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: "#ffffff",
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              Every sectional repair —{" "}
              <span style={{
              color: GOLD,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                done on the first visit.
              </span>
            </h2>
          </div>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: 17,
          color: "rgba(255,255,255,0.7)",
          lineHeight: 1.6,
          margin: 0,
          maxWidth: 480,
          justifySelf: "end"
        }}>
            Most sectional repairs we can fix without replacing your whole
            door — just the affected panel or part. Stock in the van, sorted
            same day.
          </p>
        </div>

        {/* === BENTO GRID === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(6, 1fr)",
        gap: 20
      }} className="sectional-services-grid">
          {SERVICES.map((s, i) => <ServiceCard key={i} service={s} />)}
        </div>

        {/* === BOTTOM CTA === */}
        <div style={{
        marginTop: 56,
        padding: "32px 40px",
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 4,
        display: "flex",
        flexWrap: "wrap",
        gap: 24,
        alignItems: "center",
        justifyContent: "space-between"
      }}>
          <div style={{
          fontFamily: FONT_HEAD,
          fontSize: 18,
          fontWeight: 500,
          color: "rgba(255,255,255,0.9)",
          lineHeight: 1.4
        }}>
            Got a dented panel?{" "}
            <span style={{
            color: GOLD,
            fontWeight: 700
          }}>
              Send us a photo
            </span>{" "}
            — we'll tell you if it can be swapped or repaired.
          </div>

          <a href="https://wa.me/61485000933?text=Hi%20Gold%20Garage%20Door%2C%20here%27s%20a%20photo%20of%20my%20sectional%20door%20panel." target="_blank" rel="noopener noreferrer" style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 22px",
          background: GOLD,
          color: NAVY,
          fontFamily: FONT_HEAD,
          fontSize: 14,
          fontWeight: 700,
          borderRadius: 8,
          textDecoration: "none",
          whiteSpace: "nowrap",
          transition: "transform 0.2s, box-shadow 0.2s",
          boxShadow: "0 6px 20px rgba(245,166,35,0.3)"
        }} onMouseEnter={e => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.45)";
        }} onMouseLeave={e => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.3)";
        }}>
            Send photo via WhatsApp
            <ArrowUpRight size={16} strokeWidth={2.5} />
          </a>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 1024px) {
          .sectional-services-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .sectional-services-grid > * {
            grid-column: span 1 !important;
          }
        }
        @media (max-width: 700px) {
          .sectional-services-header {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
            align-items: start !important;
          }
          .sectional-services-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>;
}
function ServiceCard({
  service
}: {
  service: Service;
}) {
  const Icon = service.icon;
  const colSpan = service.span === "wide" ? 4 : 2;
  return <div className="sectional-service-card" style={{
    gridColumn: `span ${colSpan}`,
    position: "relative",
    padding: "36px 32px",
    background: service.accent ? `linear-gradient(135deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)` : "rgba(255,255,255,0.025)",
    border: service.accent ? `1px solid ${GOLD}` : "1px solid rgba(255,255,255,0.08)",
    borderRadius: 4,
    cursor: "default",
    transition: "transform 0.3s, border-color 0.3s, background 0.3s",
    overflow: "hidden",
    minHeight: 240
  }} onMouseEnter={e => {
    const el = e.currentTarget as HTMLDivElement;
    el.style.transform = "translateY(-4px)";
    if (!service.accent) {
      el.style.borderColor = "rgba(245,166,35,0.4)";
      el.style.background = "rgba(255,255,255,0.045)";
    }
  }} onMouseLeave={e => {
    const el = e.currentTarget as HTMLDivElement;
    el.style.transform = "translateY(0)";
    if (!service.accent) {
      el.style.borderColor = "rgba(255,255,255,0.08)";
      el.style.background = "rgba(255,255,255,0.025)";
    }
  }}>
      <div style={{
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      marginBottom: 28
    }}>
        <span style={{
        fontFamily: FONT_HEAD,
        fontSize: 13,
        fontWeight: 800,
        color: service.accent ? GOLD : "rgba(255,255,255,0.4)",
        letterSpacing: "0.1em"
      }}>
          {service.number}
        </span>

        <div style={{
        width: 44,
        height: 44,
        borderRadius: 8,
        background: service.accent ? GOLD : "rgba(245,166,35,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}>
          <Icon size={22} color={service.accent ? NAVY : GOLD} strokeWidth={2} />
        </div>
      </div>

      <h3 style={{
      fontFamily: FONT_HEAD,
      fontSize: service.span === "wide" ? 24 : 20,
      fontWeight: 700,
      color: "#ffffff",
      lineHeight: 1.2,
      letterSpacing: "-0.015em",
      margin: "0 0 12px"
    }}>
        {service.title}
      </h3>

      <p style={{
      fontFamily: FONT_BODY,
      fontSize: 14,
      color: "rgba(255,255,255,0.65)",
      lineHeight: 1.55,
      margin: "0 0 20px"
    }}>
        {service.desc}
      </p>

      <ul style={{
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8
    }}>
        {service.bullets.map((b, i) => <li key={i} style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: FONT_BODY,
        fontSize: 13,
        color: "rgba(255,255,255,0.75)"
      }}>
            <span style={{
          width: 4,
          height: 4,
          borderRadius: "50%",
          background: GOLD,
          flexShrink: 0
        }} />
            {b}
          </li>)}
      </ul>

      {service.accent && <div aria-hidden style={{
      position: "absolute",
      bottom: -40,
      right: -40,
      width: 160,
      height: 160,
      borderRadius: "50%",
      background: `radial-gradient(circle, rgba(245,166,35,0.12) 0%, transparent 70%)`,
      pointerEvents: "none"
    }} />}
    </div>;
}